import ChannelTree from '@/data/ChannelTree';
import ChannelMapper from '@/data/mappers/ChannelMapper';
import Video from '@/data/Video';
import Videos from '@/data/Videos';
import VideosFetchConfig from '@/services/videos/VideosFetchConfig';
import VideosMapper from '@/data/mappers/VideosMapper';
import VideosResultConfig from '@/services/videos/VideosResultConfig';
import VideoThumbnail from '@/data/VideoThumbnail';
import VideoThumbnailsMapper from '@/data/mappers/VideoThumbnailsMapper';
import VideoDataMapper from '@/data/mappers/VideoDataMapper';
import Logger from '@/utils/Logger';
import VideoData from '@/data/VideoData';
import IVideoServiceRaw from '@/services/videos/IVideoServiceRaw';
import Player from '@/data/Player';
import EnvService from '@/services/environment/EnvService';

export default class VideoService {

  private videoServiceRaw: IVideoServiceRaw;

  public constructor(videoServiceRaw: IVideoServiceRaw) {
    this.videoServiceRaw = videoServiceRaw;
  }

  public async init() {
    await this.videoServiceRaw.init();
  }

  public hasRemoteSort() {
    return this.videoServiceRaw.hasRemoteSort();
  }

  public getStoredPlayer(): Player {
    return this.videoServiceRaw.getStoredPlayer();
  }

  public async getChannels(): Promise<ChannelTree> {
    const dataObj = await this.videoServiceRaw.getChannels();
    const channels = ChannelMapper.mapFromObjectWithChildren(dataObj);

    return channels;
  }
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  public async getChannelMetaData(channelId: number): Promise<any> {

    const dataObj = await this.videoServiceRaw.getChannelMetaData(channelId);
    return dataObj;
  }

  public async getChannelsInfoDefinition(workspace: string, absJcrPropPath: string): Promise<string> {
    return await this.videoServiceRaw.getChannelsInfoDefinition(workspace, absJcrPropPath);
  }
  
  public async getRelatedVideos(videoId: string, allowedChannelIds: Array<number>, baseChannelId?: number,
      baseChannelName?: string, locale?: string): Promise<Videos> {
    
    const fetchConfig = new VideosFetchConfig();
    const resultConfig = new VideosResultConfig();
    fetchConfig.videoIds.push(videoId);
    fetchConfig.allowedChannelIds = allowedChannelIds;
    const params = this.getUrlParams(fetchConfig, resultConfig, undefined,
      baseChannelId, baseChannelName, locale);
    
    const dataObj = await this.videoServiceRaw.getRelatedVideos(params);
    return VideosMapper.mapFromVideosObjects(dataObj, fetchConfig.listOffset);
  }

  public async getVideos(
      fetchConfig: VideosFetchConfig, resultConfig: VideosResultConfig,
      sortTypeKey?: string, baseChannelId?: number,
      baseChannelName?: string, locale?: string): Promise<Videos> {

    const params = this.getUrlParams(fetchConfig, resultConfig, sortTypeKey,
      baseChannelId, baseChannelName, locale);

    const dataObj = await this.videoServiceRaw.getVideos(params);
    const videos = VideosMapper.mapFromVideosObjects(dataObj, fetchConfig.listOffset);

    return videos;
  }

  public async searchVideos(searchTerms: Array<string>, allowedChannelIds: Array<number>,
    baseChannelId?: number, baseChannelName?: string, locale?: string): Promise<Videos> {
    
    const fetchConfig = new VideosFetchConfig();
    const resultConfig = new VideosResultConfig();
    fetchConfig.searchTerms.push(...searchTerms);
    fetchConfig.allowedChannelIds = allowedChannelIds;
    const params = this.getUrlParams(fetchConfig, resultConfig, undefined,
      baseChannelId, baseChannelName, locale);
    
    const dataObj = await this.videoServiceRaw.getVideos(params);
    return VideosMapper.mapFromVideosObjects(dataObj, fetchConfig.listOffset);
  }

  public async getThumbnails(vmId: number|undefined, videoId: string): Promise<VideoThumbnail[]>{
    
    const dataObj = await this.videoServiceRaw.getThumbnails(videoId);
    return VideoThumbnailsMapper.mapFromObjectArray(dataObj);
  }

  public async fetchThumbnailsForVideo(vmId: number|undefined, video: Video): Promise<void> {
    
    video.thumbnails = this.getThumbnails(vmId, video.id);
    await video.thumbnails;
  }

  public async getThumbnailsForVideos(vmId: number|undefined, videos: Videos): Promise<void> {
    
    const thumbnailPromises = new Array<Promise<VideoThumbnail[]>>();
    for(const video of videos.videos) {
      if(!video) continue;

      const thumbnails = this.getThumbnails(vmId, video.id).catch(error => {
        // TODO
        Logger.error(error.message);
      });
      if(!thumbnails) continue;
      video.thumbnails = thumbnails as Promise<VideoThumbnail[]>;
      thumbnailPromises.push(video.thumbnails as Promise<VideoThumbnail[]>);
    }
    await Promise.all(thumbnailPromises);
  }

  public async getVideoData(videoId: string): Promise<VideoData> {
    const dataObj = await this.videoServiceRaw.getVideoData(videoId);
    const videoData = VideoDataMapper.mapFromVideoDataObj(dataObj);

    try {
      videoData.relatedVideos = VideoDataMapper.mapRelatedVideosFromVideoDataObj(dataObj, undefined);
    }
    catch(error) {
      error instanceof Error
        ? Logger.error(error.message)
        : Logger.errorAny(error);
    }
    return videoData;
  }

  public async getVideoMetaData(videoId: string, resultConfig?: VideosResultConfig) {
    const videosFetchConfig = new VideosFetchConfig();
    videosFetchConfig.videoIds.push(videoId);
    const videosMeta = await this.getVideos(
      videosFetchConfig, resultConfig ? resultConfig : new VideosResultConfig());
    if(videosMeta.total !== 1 || videosMeta?.videos?.length !== 1) {
      const msg = `Got invalid count of videos when querying data for video with id ${videoId}.`
      
      if(videosMeta.total < 1 || videosMeta?.videos?.length === 1) {
        throw new Error(msg);
      }
      Logger.warn(msg);
    }
    return videosMeta.videos[0];
  }

  private getUrlParams(fetchConfig: VideosFetchConfig, resultConfig: VideosResultConfig,
    sortTypeKey?: string, baseChannelId?: number,
    baseChannelName?: string, locale?: string) {

      const params = new URLSearchParams();
      for(const videoId of fetchConfig.videoIds) {
        params.append('video_ids', videoId);
      }
      for(const channelId of fetchConfig.channelIds) {
        params.append('channel_id[]', channelId.toString());
      }
      for(const channelId of fetchConfig.allowedChannelIds) {
        params.append('allowed_channel_id[]', channelId.toString());
      }
      for(const searchTerm of fetchConfig.searchTerms) {
        params.append('search_term', searchTerm);
      }
      if(fetchConfig.searchField) {
        params.append('search_in_field', fetchConfig.searchField);
      }
  
      let listOffset = fetchConfig.listOffset;
      let maxCount = fetchConfig.maxCount;
      if(!this.videoServiceRaw.hasRemoteSort()) {
        listOffset = VideosFetchConfig.defaultListOffset;
        maxCount = VideosFetchConfig.defaultMaxCount;
      }
  
      params.append('offset', listOffset.toString());
      params.append('limit', maxCount.toString());
  
      params.append('include_custom_metadata', resultConfig.cutomMetaData.toString());
      if(resultConfig.cutomMetaData) {
        for(const field of resultConfig.cutomMetaDataFields) {
          params.append('custom_metadata_field', field);
        }
      }
      params.append('include_keywords', resultConfig.keywords.toString());
      params.append('include_channel_assignments', resultConfig.channels.toString());
  
      if(sortTypeKey && this.videoServiceRaw.hasRemoteSort()) {
        params.append('custom_sort', sortTypeKey);
      }
  
      if(baseChannelId && baseChannelName) {
        params.append('base_channel_id', baseChannelId.toString());
        params.append('base_channel_name', baseChannelName);
      }
      if(locale) {
        params.append('base_channel_locale', locale);
      }
  
      if(EnvService.hasEnvVar('VUE_APP_USE_STATIC_CONTENT') &&
          EnvService.getEnvVar('VUE_APP_USE_STATIC_CONTENT') === 'true') {
        //only supported with Magnolia backend.
        params.append('fetch_static_content_for_tests', 'true');
      }
      return params;
  }
}
